import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Car, Truck } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET  = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop    = (barRect.top - containerRect.top) - OFFSET - tipH; // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;      // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), clamp considerando meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function VehicleProductionChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null);   // `${index}-auto` | `${index}-commercial`
  const [tooltipData, setTooltipData] = useState(null);        // { year, value, label }
  const [tooltipPos,  setTooltipPos]  = useState(null);        // { x, y, placeAbove }
  const [isMobile,    setIsMobile]    = useState(false);

  // Refs de layout
  const scrollRef       = useRef(null);
  const innerRefDesktop = useRef(null);
  const innerRefMobile  = useRef(null);
  const tooltipRef      = useRef(null);

  const productionData = [
    { year: 2010, auto: 2.68, commercial: 0.47 }, { year: 2011, auto: 2.63, commercial: 0.51 },
    { year: 2012, auto: 2.76, commercial: 0.47 }, { year: 2013, auto: 2.96, commercial: 0.53 },
    { year: 2014, auto: 2.51, commercial: 0.47 }, { year: 2015, auto: 2.02, commercial: 0.32 },
    { year: 2016, auto: 1.80, commercial: 0.30 }, { year: 2017, auto: 2.31, commercial: 0.33 },
    { year: 2018, auto: 2.39, commercial: 0.36 }, { year: 2019, auto: 2.45, commercial: 0.36 },
    { year: 2020, auto: 1.61, commercial: 0.30 }, { year: 2021, auto: 1.71, commercial: 0.36 },
    { year: 2022, auto: 1.82, commercial: 0.36 }, { year: 2023, auto: 1.78, commercial: 0.42 },
    { year: 2024, auto: 1.90, commercial: 0.48 }
  ];

  const CHART_HEIGHT = 320; // px
  const maxValue = Math.max(...productionData.map(d => Math.max(d.auto, d.commercial)));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedData(productionData), 400);
    return () => clearTimeout(timer);
  }, []);

  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 40);

  const clsAuto = (active) =>
    `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'}`;
  const clsComm = (active) =>
    `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-purple-600' : 'bg-purple-500 hover:bg-purple-600'}`;

  // Mostrar tooltip com posicionamento seguro (usa duplo RAF p/ mobile Safari)
  const showTooltip = (item, key, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect       = targetEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setHoveredBarKey(key);

    const isAuto = key.endsWith('auto');
    const value  = isAuto ? item.auto : item.commercial;
    const label  = isAuto ? 'Automóveis' : 'Comerciais Leves';

    // 1) Garante render do tooltip
    setTooltipData({ year: item.year, value, label });

    // 2) Medição no próximo frame (e +1 frame para iOS/Safari)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tip = tooltipRef.current;
        const tipW = tip ? tip.offsetWidth  : 140;
        const tipH = tip ? tip.offsetHeight : 44;

        const { x, y, placeAbove } = computeSafeTooltipPosition({
          barRect,
          containerRect,
          tipW,
          tipH,
          prefAbove: !isMobile // no mobile preferimos abaixo
        });

        setTooltipPos({ x, y, placeAbove });
      });
    });
  };

  const handleEnter = (item, key) => (e) => showTooltip(item, key, e.currentTarget);
  const handleLeave = () => {
    setHoveredBarKey(null);
    setTooltipData(null);
    setTooltipPos(null);
  };

  // Handlers com Pointer Events (cobrem mouse e touch)
  const getBarHandlers = (item, key) => ({
    onPointerDown:  (e) => showTooltip(item, key, e.currentTarget),             // tap/click
    onPointerEnter: (e) => !isMobile && showTooltip(item, key, e.currentTarget),// hover desktop
    onPointerMove:  (e) => !isMobile && showTooltip(item, key, e.currentTarget),
    onPointerLeave: handleLeave,
  });

  // --- mobile layout ---
  const BAR_W_MOBILE = 16;
  const GAP_BETWEEN_BARS = 6;
  const GAP_BETWEEN_GROUPS = 12;
  const GROUP_W_MOBILE = (BAR_W_MOBILE * 2) + GAP_BETWEEN_BARS + GAP_BETWEEN_GROUPS;
  const contentWidthMobile = (productionData.length * GROUP_W_MOBILE) + 16;

  const touchSafeStyle = { touchAction: 'manipulation', userSelect: 'none' };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Evolução da Produção de Veículos</CardTitle>
          <p className="text-center text-gray-600">Produção de automóveis e comerciais leves no Brasil (em milhões)</p>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            // --- MOBILE ---
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile, paddingTop: 6 }}
              >
                {productionData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: GROUP_W_MOBILE }}>
                    <div className="flex items-end" style={{ gap: GAP_BETWEEN_BARS }}>
                      {/* Automóveis */}
                      <div
                        className={clsAuto(hoveredBarKey === `${index}-auto`)}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.auto) : 0}px`,
                          transitionDelay: `${index * 40}ms`,
                          transform: hoveredBarKey === `${index}-auto` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-auto`)}
                      />
                      {/* Comerciais leves */}
                      <div
                        className={clsComm(hoveredBarKey === `${index}-commercial`)}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.commercial) : 0}px`,
                          transitionDelay: `${index * 40 + 16}ms`,
                          transform: hoveredBarKey === `${index}-commercial` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-commercial`)}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">{tooltipData.label}: {tooltipData.value.toFixed(2)} M</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP ---
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {productionData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div className="flex items-end gap-1 h-full">
                      {/* Automóveis */}
                      <div
                        className={clsAuto(hoveredBarKey === `${index}-auto`) + " w-4"}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.auto) : 0}px`,
                          transitionDelay: `${index * 50}ms`,
                          transform: hoveredBarKey === `${index}-auto` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-auto`)}
                      />
                      {/* Comerciais leves */}
                      <div
                        className={clsComm(hoveredBarKey === `${index}-commercial`) + " w-4"}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.commercial) : 0}px`,
                          transitionDelay: `${index * 50 + 20}ms`,
                          transform: hoveredBarKey === `${index}-commercial` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-commercial`)}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">{tooltipData.label}: {tooltipData.value.toFixed(2)} M</div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 flex justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded" />
              <Car className="w-4 h-4 mr-1" />
              <span>Automóveis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-500 rounded" />
              <Truck className="w-4 h-4 mr-1" />
              <span>Comerciais Leves</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards analíticos (mantidos) */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <Car className="w-6 h-6" />
              Automóveis: Cenário de Recuperação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Impacto da Crise (2014-2016)</h4>
              <p className="text-sm text-gray-700">
                A produção despencou de 2,96 milhões (2013) para apenas 1,8 milhão (2016), uma queda de 28%,
                reflexo da crise econômica nacional e da desaceleração do consumo interno.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Retomada Gradual (2024)</h4>
              <p className="text-sm text-gray-700">
                Com 1,9 milhão de unidades em 2024 (+6,6% vs 2023), o setor mostra sinais de estabilização,
                mas ainda enfrenta desafios da competição com veículos importados eletrificados.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-purple-900 flex items-center gap-2">
              <Truck className="w-6 h-6" />
              Comerciais Leves: Maior Resiliência
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">Estabilidade Relativa</h4>
              <p className="text-sm text-gray-700">
                Crescimento modesto de 0,2% entre 2010-2024, mas com recuperação mais sólida:
                481 mil unidades em 2024, o melhor resultado desde 2013.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">Forte Crescimento Recente</h4>
              <p className="text-sm text-gray-700">
                Crescimento de 14,3% entre 2023-2024, impulsionado pela demanda do e-commerce,
                logística urbana e renovação de frotas empresariais.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-amber-900">Contexto e Perspectivas do Setor Automotivo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Ranking Mundial</h4>
              <p className="text-sm text-gray-700">
                Brasil retomou a 8ª posição mundial em produção de veículos em 2024,
                com 2,56 milhões de unidades (+9,7% vs 2023).
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Investimentos Recordes</h4>
              <p className="text-sm text-gray-700">
                Setor passa por ciclo de R$ 180 bilhões em investimentos,
                focados em modernização industrial e eletrificação.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Desafios da Eletrificação</h4>
              <p className="text-sm text-gray-700">
                Crescimento de importações de veículos elétricos da China desafia
                a competitividade da produção nacional.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
